import { Immutable } from "immer";
import React from "react";
import { ProductReviewsSSR } from "tsi-common-react/src/apps/reviews/ProductReviewsSSR/index";

import type {
    FullReviewsModule as FullReviewsModuleValue,
    templates,
} from "@reactivated";

export type IFullReviewsModuleProps = Immutable<{
    value: FullReviewsModuleValue;
    reviews: templates.ProductReviewsTemplate | null;
    page_num_reviews: number;
}>;

type IProps = IFullReviewsModuleProps;

/**
 * Standalone (Phase 1) port of the legacy
 * `cms/components/pdp-reviews-full.html` (#33480):
 *
 * - The `.pdp-section` wrapper ALWAYS renders (the legacy `{% if
 *   page.reviews_brand_id %}` gate sat INSIDE it).
 * - `props.reviews` is the page-derived SSR bundle from the
 *   `get_full_reviews_props` templatetag; it is `null` when the page gate
 *   fails, so nothing inside the wrapper renders (legacy parity). The extra
 *   `reviews_brand_id` check mirrors toppers' consolidated `reviews_full`
 *   component-map entry.
 * - `ProductReviewsSSR` gets `brand="Sealy"` + `isCollapsedView={true}`,
 *   matching this site's legacy `ProductReviewsTemplate.tsx` re-export (the
 *   island the legacy `{% react_product_reviews %}` tag mounted).
 * - The CTA gates on `value.show_cta && page_num_reviews > 0` -- the legacy
 *   `page.num_reviews` page-level simple sum, NOT the bundle-deduped
 *   `reviews.num_reviews`.
 * - The CTA href uses the Link snippet's resolved `url` (mirroring toppers'
 *   port): the legacy `{% if value.cta.link_page %}{{
 *   value.cta.link_page.get_url }}{% else %}{{ value.cta }}{% endif %}`
 *   rendered `str(Link)` (a debug-ish "Link[...]: url" string) for non-page
 *   links, which was a latent template bug; `cta.url` resolves every link
 *   type correctly.
 */
export const FullReviewsModule = (props: IProps): React.JSX.Element => {
    const reviews = props.reviews;
    return (
        <div className="pdp-section">
            {reviews && reviews.reviews_brand_id && (
                <>
                    <div id="reviews">
                        <ProductReviewsSSR
                            {...reviews}
                            brand="Sealy"
                            isCollapsedView={true}
                        />
                    </div>
                    {props.value.show_cta && props.page_num_reviews > 0 && (
                        <div className="pdp-reviews-full__cta">
                            <a
                                className="button button--ghost pdp-reviews-full__cta-link"
                                href={props.value.cta?.url ?? ""}
                            >
                                {props.value.cta_text}
                            </a>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
